import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from './ui/select';
import { useBoards } from '../context/BoardContext';
import { updateBoard } from '../lib/api';
import { toast } from '../hooks/use-toast';
import {
  LEGEND_PALETTE,
  DEFAULT_LEGEND,
  CATEGORIES,
  CATEGORY_MAP,
  FIELD_TYPES,
  getCategoryConfig,
} from '../lib/categories';
import { Users, UserPlus, X } from 'lucide-react';
import { updateTeamMembers } from '../lib/api';

const cloneConfig = (config) => JSON.parse(JSON.stringify(config));

const StatusEditor = ({ statuses, onChange }) => {
  const addStatus = () => {
    const palette = LEGEND_PALETTE[statuses.length % LEGEND_PALETTE.length];
    onChange([
      ...statuses,
      {
        key: `status_${Date.now()}`,
        label: 'New status',
        color: palette.color,
        bg: palette.bg,
        ring: palette.ring,
        condition: '',
      },
    ]);
  };

  const removeStatus = (idx) => {
    if (statuses.length <= 1) {
      toast({ title: 'At least one status is required' });
      return;
    }
    onChange(statuses.filter((_, i) => i !== idx));
  };

  const updateStatus = (idx, patch) => {
    onChange(statuses.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  return (
    <div className="space-y-2">
      {statuses.map((entry, idx) => (
        <div key={idx} className="rounded-md border border-slate-200 p-3 bg-slate-50/60">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-md border-2 flex-shrink-0"
              style={{ background: entry.bg, borderColor: entry.ring }}
            />
            <div className="flex-1 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Label</Label>
                  <Input
                    value={entry.label}
                    onChange={(e) => updateStatus(idx, { label: e.target.value })}
                    className="h-8 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Key</Label>
                  <Input
                    value={entry.key}
                    onChange={(e) => updateStatus(idx, { key: e.target.value })}
                    className="h-8 mt-1"
                    disabled={['green', 'yellow', 'red', 'grey'].includes(entry.key)}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Color</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {LEGEND_PALETTE.map((p, i) => {
                    const selected = entry.color === p.color;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() =>
                          updateStatus(idx, {
                            color: p.color,
                            bg: p.bg,
                            ring: p.ring,
                          })
                        }
                        className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                          selected
                            ? 'border-slate-900 ring-2 ring-offset-1 ring-slate-400'
                            : 'border-white shadow-sm'
                        }`}
                        style={{ background: p.color }}
                      />
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Condition</Label>
                <Input
                  value={entry.condition || ''}
                  onChange={(e) => updateStatus(idx, { condition: e.target.value })}
                  placeholder="When is this color used?"
                  className="h-8 mt-1"
                />
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeStatus(idx)}
              className="text-red-500 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addStatus}>
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add status
      </Button>
    </div>
  );
};

const FieldEditor = ({ fields, onChange }) => {
  const addField = () => {
    onChange([
      ...fields,
      { name: `field_${Date.now()}`, label: 'New field', type: 'text', options: [] },
    ]);
  };
  const removeField = (idx) => onChange(fields.filter((_, i) => i !== idx));
  const updateField = (idx, patch) =>
    onChange(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

  return (
    <div className="space-y-2">
      {fields.length === 0 && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-md py-3 px-3 text-center">
          No fields yet. Add fields users will fill in when an issue is reported.
        </div>
      )}
      {fields.map((f, idx) => (
        <div key={idx} className="rounded-md border border-slate-200 p-3 bg-slate-50/60 space-y-2">
          <div className="grid grid-cols-6 gap-2">
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Label</Label>
              <Input
                value={f.label}
                onChange={(e) => updateField(idx, { label: e.target.value })}
                className="h-8 mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Key</Label>
              <Input
                value={f.name}
                onChange={(e) => updateField(idx, { name: e.target.value })}
                className="h-8 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Type</Label>
              <Select
                value={f.type}
                onValueChange={(v) => updateField(idx, { type: v })}
              >
                <SelectTrigger className="h-8 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="multiselect">Multi-select</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeField(idx)}
                className="text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {(f.type === 'select' || f.type === 'multiselect') && (
            <div>
              <Label className="text-xs text-slate-500">
                {f.type === 'multiselect' ? 'Multi-select options (one per line)' : 'Dropdown options (one per line)'}
              </Label>
              <Textarea
                value={(f.options || []).join('\n')}
                onChange={(e) =>
                  updateField(idx, {
                    options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                  })
                }
                rows={3}
                className="mt-1"
              />
            </div>
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addField}>
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add field
      </Button>
    </div>
  );
};

const TeamEditor = () => {
  const { activeBoard, updateActiveBoard } = useBoards();
  const [members, setMembers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeBoard) {
      const list = (activeBoard.team_members || []).map((m) =>
        typeof m === 'string'
          ? { email: m, role: 'user', assigned_categories: CATEGORIES.map((c) => c.key) }
          : {
              email: m.email,
              role: m.role || 'user',
              assigned_categories: m.assigned_categories || CATEGORIES.map((c) => c.key),
            }
      );
      setMembers(list);
    }
  }, [activeBoard]);

  if (!activeBoard) return null;

  const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

  const addMember = () => {
    const e = newEmail.trim().toLowerCase();
    if (!validEmail(e)) {
      toast({ title: 'Invalid email' });
      return;
    }
    if (members.some((m) => m.email === e)) {
      toast({ title: 'Already added' });
      return;
    }
    setMembers([
      ...members,
      { email: e, role: 'user', assigned_categories: CATEGORIES.map((c) => c.key) },
    ]);
    setNewEmail('');
  };

  const removeMember = (email) => {
    setMembers(members.filter((m) => m.email !== email));
  };

  const updateMember = (email, patch) => {
    setMembers(members.map((m) => (m.email === email ? { ...m, ...patch } : m)));
  };

  const toggleCategory = (email, catKey) => {
    setMembers(
      members.map((m) => {
        if (m.email !== email) return m;
        const cats = new Set(m.assigned_categories || []);
        if (cats.has(catKey)) cats.delete(catKey);
        else cats.add(catKey);
        return { ...m, assigned_categories: Array.from(cats) };
      })
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await updateTeamMembers(activeBoard.id, members);
      updateActiveBoard(updated);
      toast({ title: 'Team saved' });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast({ title: 'Failed', description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
          <Users className="h-4 w-4" />
          Team members
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Board owners have full access. Users can only edit issues & attach analysis templates
          in their assigned SQPDCHI categories.
        </p>
        <div className="flex gap-2">
          <Input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMember())}
            placeholder="colleague@company.com"
            className="flex-1"
          />
          <Button onClick={addMember} variant="outline">
            <UserPlus className="h-4 w-4 mr-1.5" />
            Add
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-slate-600">
          Board owner:{' '}
          <span className="font-semibold text-slate-800">{activeBoard.owner_email}</span>{' '}
          <span className="text-slate-400">(full access)</span>
        </div>
        {members.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-md py-4 px-3 text-center">
            No additional team members yet. Add colleagues above.
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.email} className="rounded-lg border border-slate-200 p-3 bg-white space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                    {m.email[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{m.email}</div>
                  </div>
                  <Select value={m.role} onValueChange={(v) => updateMember(m.email, { role: v })}>
                    <SelectTrigger className="h-8 w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeMember(m.email)}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {m.role === 'user' && (
                  <div>
                    <div className="text-xs text-slate-500 mb-1.5">
                      Assigned categories (user can only edit these)
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {CATEGORIES.map((cat) => {
                        const active = (m.assigned_categories || []).includes(cat.key);
                        return (
                          <button
                            key={cat.key}
                            type="button"
                            onClick={() => toggleCategory(m.email, cat.key)}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-md border-2 text-xs font-semibold transition-all ${
                              active
                                ? 'border-slate-900 shadow-sm'
                                : 'border-slate-200 opacity-40'
                            }`}
                            style={{
                              background: active ? cat.color + '20' : 'white',
                              color: active ? cat.color : '#64748b',
                            }}
                          >
                            <div
                              className="w-4 h-4 rounded flex items-center justify-center text-white text-[9px] font-bold"
                              style={{ background: cat.color }}
                            >
                              {cat.key}
                            </div>
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {m.role === 'owner' && (
                  <div className="text-xs text-slate-500 italic">
                    Full access to all categories on this board.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
        >
          {saving ? 'Saving...' : 'Save team'}
        </Button>
      </div>
    </div>
  );
};

const BoardSettingsDialog = ({ open, onOpenChange, onSaved }) => {
  const { activeBoard, updateActiveBoard } = useBoards();
  const [configs, setConfigs] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeCat, setActiveCat] = useState('S');

  useEffect(() => {
    if (open && activeBoard) {
      const initial = {};
      CATEGORIES.forEach((c) => {
        initial[c.key] = cloneConfig(getCategoryConfig(activeBoard, c.key));
      });
      setConfigs(initial);
      setActiveCat('S');
    }
  }, [open, activeBoard]);

  if (!activeBoard) return null;

  const setCategoryStatuses = (catKey, statuses) => {
    setConfigs((prev) => ({
      ...prev,
      [catKey]: { ...prev[catKey], statuses },
    }));
  };

  const setCategoryFields = (catKey, fields) => {
    setConfigs((prev) => ({
      ...prev,
      [catKey]: { ...prev[catKey], fields },
    }));
  };

  const resetCategory = (catKey) => {
    setConfigs((prev) => ({
      ...prev,
      [catKey]: {
        statuses: cloneConfig(DEFAULT_LEGEND),
        fields: [],
      },
    }));
  };

  const applyToAll = () => {
    const src = configs[activeCat];
    if (!src) return;
    const next = {};
    CATEGORIES.forEach((c) => {
      next[c.key] = { statuses: cloneConfig(src.statuses), fields: cloneConfig(src.fields) };
    });
    setConfigs(next);
    toast({ title: `Applied ${CATEGORY_MAP[activeCat].label} config to all categories` });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await updateBoard(activeBoard.id, { category_configs: configs });
      updateActiveBoard(updated);
      toast({ title: 'Board settings saved' });
      onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Failed', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[820px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Board Settings — Category Configuration</DialogTitle>
          <DialogDescription>
            Set status colors and detail fields for each SQPDCHI category. Changes apply only to this board.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeCat} onValueChange={setActiveCat}>
          <TabsList className="grid grid-cols-8 mb-4">
            <TabsTrigger
              value="_team"
              className="flex items-center gap-1.5 data-[state=active]:bg-white"
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline text-xs">Team</span>
            </TabsTrigger>
            {CATEGORIES.map((c) => (
              <TabsTrigger
                key={c.key}
                value={c.key}
                className="flex items-center gap-1.5 data-[state=active]:bg-white"
              >
                <div
                  className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ background: c.color }}
                >
                  {c.key}
                </div>
                <span className="hidden sm:inline text-xs">{c.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="_team" className="space-y-4">
            <TeamEditor />
          </TabsContent>

          {CATEGORIES.map((c) => {
            const cfg = configs[c.key] || { statuses: DEFAULT_LEGEND, fields: [] };
            return (
              <TabsContent key={c.key} value={c.key} className="space-y-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-9 h-9 rounded-md flex items-center justify-center text-white font-bold shadow-sm"
                      style={{ background: c.color }}
                    >
                      {c.key}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{c.label}</div>
                      <div className="text-xs text-slate-500">
                        {cfg.statuses.length} statuses · {cfg.fields.length} detail fields
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => resetCategory(c.key)}>
                      Reset defaults
                    </Button>
                    <Button variant="ghost" size="sm" onClick={applyToAll}>
                      Apply to all
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-slate-800 mb-2">
                    Legend / Status colors
                  </div>
                  <StatusEditor
                    statuses={cfg.statuses}
                    onChange={(s) => setCategoryStatuses(c.key, s)}
                  />
                </div>

                <div>
                  <div className="text-sm font-semibold text-slate-800 mb-2">
                    Detail fields (shown when a card is marked as an issue)
                  </div>
                  <FieldEditor
                    fields={cfg.fields}
                    onChange={(f) => setCategoryFields(c.key, f)}
                  />
                </div>
              </TabsContent>
            );
          })}
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
          >
            {saving ? 'Saving...' : 'Save all settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BoardSettingsDialog;
